import React, { forwardRef, useEffect, useState } from 'react';
import { CustomModal, BuySellDetailsMRT, LimitBanner } from '@components';
import {
    Box,
    FormControl,
    InputAdornment,
    InputLabel,
    FormHelperText,
    OutlinedInput,
    Select,
    Typography,
    MenuItem
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import {
    apiBuyContract,
    apiBuyTheEurContractMinMax,
    apiGetMinMaxRaw,
    apiGetTheEur,
    apiGetMinMaxDisplay,
    apiGetOrder
} from '@store';
import { showToast, getMargin } from '@utils';
import { useLimitCheck } from '@hooks';

export const Sell = forwardRef(
    ({ handleClose, selectedContract, user, sym }, ref) => {
        const dispatch = useDispatch();
        const [showMinMaxInput, setShowMinMaxInput] = useState(false);
        const { hedgeMargin } = useSelector((state) => state.hedgeMargin);
        const [submitting, setSubmitting] = useState(false);
        const [showVolumeError, setShowVolumeError] = useState(false);

        const [state, setState] = useState({
            sym: selectedContract?.contract || sym,
            starthour: user?.StartTime,
            endhour: user?.EndTime,
            lunchstart: user?.LunchStart,
            lunchend: user?.LunchEnd,
            Hideflex: user?.Hideflex,
            Company: user?.Company,
            CompanyShort: user?.CompanyShort,
            UserCanBuyFrom: user?.CanBuyFrom,
            UserCanSellTo: user?.CanSellTo,
            Endpoints: user?.Endpoints,
            Currency: 'EUR',
            market: 'THE',
            type: 'sell',
            price_selected: '',
            buyer: '',
            seller: user?.CompanyShort,
            deliverypoint: 'THE',
            price_percent: '100',
            volume: '',
            exitpoint: '',
            ordertype: 'internal',
            currency: 'EUR',
            max_price: '',
            on_max: false,
            min_price: '',
            on_min: false
        });

        const [margin, setMargin] = useState(0);

        useEffect(() => {
            setMargin(
                getMargin({
                    contractName: selectedContract?.contract || sym,
                    hedgeMargin,
                    volume: state.volume
                })
            );
        }, [selectedContract, sym, hedgeMargin, state.volume]);

        useEffect(() => {
            if (user?.CanSellTo?.length > 0) {
                setState((prevState) => ({
                    ...prevState,
                    buyer: user.CanSellTo[0]
                }));
            }
        }, [user]);

        const handleChange = (e) => {
            const { name, value } = e.target;
            setState({
                ...state,
                [name]: value
            });
            if (name === 'volume') {
                const vol = parseFloat(value);
                setShowVolumeError(value !== '' && !(vol > 0));
            }
        };

        const [type, setType] = useState('bid');

        const handleTypeChange = (e) => {
            setType(e.target.value);
            if (e.target.value === 'min_max') {
                setShowMinMaxInput(true);
            } else if (e.target.value === 'bid') {
                setShowMinMaxInput(false);
            }
        };

        const handleMinChange = (e) => {
            // If min price is changed, clear max price and disable it
            // If min price is cleared, enable max price
            if (e.target.value === '') {
                setState({
                    ...state,
                    min_price: e.target.value,
                    max_price: '',
                    on_min: false,
                    on_max: false
                });
            } else {
                setState({
                    ...state,
                    min_price: e.target.value,
                    max_price: '',
                    on_min: true,
                    on_max: false
                });
            }
        };

        const handleMaxChange = (e) => {
            // If max price is changed, clear min price and disable it
            // If max price is cleared, enable min price
            if (e.target.value === '') {
                setState({
                    ...state,
                    max_price: e.target.value,
                    min_price: '',
                    on_max: false,
                    on_min: false
                });
            } else {
                setState({
                    ...state,
                    max_price: e.target.value,
                    min_price: '',
                    on_max: true,
                    on_min: false
                });
            }
        };

        const handleSubmit = async (e) => {
            e.preventDefault();
            if (submitting) return;
            setSubmitting(true);
            let data;
            const volume = parseFloat(state.volume);
            if (!(volume > 0)) {
                setShowVolumeError(true);
                showToast('error', 'Volume must be greater than 0');
                setSubmitting(false);
                return;
            }
            if (type === 'bid') {
                // Remove min_max fields
                const { minprice, maxprice, on_min, on_max, ...rest } = state;
                const volume = parseFloat(state.volume);
                const bid = parseFloat(selectedContract.bid);
                const ask = parseFloat(selectedContract.ask);
                const updated_at = selectedContract?.updated_at;
                const buyback10 = parseFloat(selectedContract.buyback10);
                const buyback20 = parseFloat(selectedContract.buyback20);
                const buyback30 = parseFloat(selectedContract.buyback30);

                const data = {
                    ...rest,
                    margin: margin,
                    total_price: bid + margin,
                    volume,
                    exitpoint: state.exitpoint || {},
                    price: {
                        ask: ask,
                        bid: bid,
                        updated_at: updated_at,
                        buyback10: buyback10,
                        buyback20: buyback20,
                        buyback30: buyback30
                    }
                };

                const resp = await dispatch(apiBuyContract(data));
                if (resp?.meta?.requestStatus === 'fulfilled') {
                    handleClose();
                    dispatch(apiGetOrder());
                    showToast('success', 'Sales Order Saved');
                } else if (resp?.meta?.requestStatus === 'rejected') {
                    showToast(
                        'error',
                        resp?.payload || 'Error buying contract'
                    );
                }
            } else if (type === 'min_max') {
                const minprice = parseFloat(state.min_price);
                const maxprice = parseFloat(state.max_price);

                data = {
                    ...state,
                    margin: margin,
                    total_price: selectedContract?.bid + margin,
                    exitpoint: state.exitpoint || {},
                    min_price: minprice || 0,
                    max_price: maxprice || 0,
                    on_min: minprice ? true : false,
                    on_max: maxprice ? true : false
                };

                const resp = await dispatch(apiBuyTheEurContractMinMax(data));
                if (resp?.meta?.requestStatus === 'fulfilled') {
                    handleClose();
                    dispatch(apiGetMinMaxRaw());
                    dispatch(apiGetTheEur());
                    dispatch(
                        apiGetMinMaxDisplay({
                            product: 'THE',
                            currency: 'EUR'
                        })
                    );
                    dispatch(apiGetOrder());
                    showToast('success', 'Sales Order Saved');
                } else if (resp?.meta?.requestStatus === 'rejected') {
                    showToast(
                        'error',
                        resp?.payload || 'Error buying contract'
                    );
                }
            }
            setSubmitting(false);
        };

        // timer
        const [timer, setTimer] = useState(25);

        useEffect(() => {
            const interval = setInterval(() => {
                setTimer((timer) => timer - 1);
            }, 1000);
            return () => clearInterval(interval);
        }, [timer]);

        useEffect(() => {
            if (timer === 0) {
                handleClose();
            }
        }, [timer, handleClose]);

        useEffect(() => {
            if (ref?.current) {
                setTimer(25);
            }
        }, [ref]);

        const notionalValue = state?.volume
            ? selectedContract?.contract_hours *
              (parseFloat(selectedContract?.bid) - margin) *
              parseFloat(state.volume)
            : 0;

        const limitCheck = useLimitCheck({
            action: 'sell',
            currency: 'eur',
            notionalValue,
            volume: state.volume
        });

        return (
            <CustomModal
                handleClose={handleClose}
                handleSubmit={handleSubmit}
                timer={timer}
                modalTitle={`Sell Contract`}
                modalTitleColor={'#EB6969'}
                buttonText={'Sell'}
                className="modal-button-danger"
                disabled={
                    submitting ||
                    !(parseFloat(state.volume) > 0) ||
                    limitCheck.isDisabled
                }
                ref={ref}
            >
                <Box className="flex flex-col gap-3">
                    <LimitBanner messages={limitCheck.messages} />
                    <FormControl fullWidth>
                        <InputLabel
                            id="demo-simple-select-label"
                            sx={{ bgcolor: '#F5F5F5', pr: 1 }}
                        >
                            Please Select what you would like to sell
                        </InputLabel>
                        <Select
                            labelId="demo-simple-select-label"
                            id="demo-simple-select"
                            label="Type"
                            name="type"
                            value={type}
                            onChange={handleTypeChange}
                        >
                            <MenuItem value="bid">Bid Price</MenuItem>
                            <MenuItem value="min_max">Min/Max Stop</MenuItem>
                        </Select>
                    </FormControl>
                    <Box className="flex gap-3">
                        <Typography>Contract:</Typography>
                        <Typography color={'black'}>
                            {selectedContract?.contract || sym}
                        </Typography>
                    </Box>
                    <FormControl
                        fullWidth
                        variant="outlined"
                        error={showVolumeError}
                    >
                        <InputLabel
                            id="contractLabel"
                            sx={{ bgcolor: '#F5F5F5', p: '0 3px' }}
                        >
                            Volume
                        </InputLabel>
                        <OutlinedInput
                            id="volume"
                            label="Volume"
                            type="number"
                            name="volume"
                            value={state.volume}
                            onChange={handleChange}
                            inputProps={{
                                step: 0.01,
                                min: 0
                            }}
                            required
                            endAdornment={
                                <InputAdornment position="start" sx={{ ml: 2 }}>
                                    MW
                                </InputAdornment>
                            }
                        />
                        <FormHelperText>
                            {showVolumeError
                                ? 'Volume must be greater than 0'
                                : ''}
                        </FormHelperText>
                    </FormControl>
                    <FormControl fullWidth>
                        <InputLabel
                            id="demo-simple-select-label"
                            sx={{ bgcolor: '#F5F5F5', pr: 1 }}
                        >
                            Endpoint
                        </InputLabel>
                        <Select
                            labelId="demo-simple-select-label"
                            id="demo-simple-select"
                            label="Endpoint"
                            name="exitpoint"
                            value={state.exitpoint}
                            onChange={handleChange}
                        >
                            {user?.Endpoints?.map((endpoint, index) => (
                                <MenuItem key={index} value={endpoint}>
                                    {' '}
                                    {endpoint.name} - {endpoint.location}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <FormControl fullWidth>
                        <InputLabel
                            id="demo-simple-select-label"
                            sx={{ bgcolor: '#F5F5F5', pr: 1 }}
                        >
                            Sell To
                        </InputLabel>
                        <Select
                            labelId="demo-simple-select-label"
                            id="demo-simple-select"
                            label="Sell To"
                            name="buyer"
                            value={state?.buyer}
                            onChange={handleChange}
                        >
                            {user?.CanSellTo?.map((seller, index) => (
                                <MenuItem key={index} value={seller}>
                                    {seller}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <FormControl fullWidth variant="outlined" disabled>
                        <InputLabel
                            id="deliverypointLabel"
                            sx={{ bgcolor: '#F5F5F5', p: '0 3px' }}
                        >
                            Delivery Point
                        </InputLabel>
                        <OutlinedInput
                            id="deliverypoint"
                            label="Delivery Point"
                            name="deliverypoint"
                            value={state.deliverypoint || 'THE'}
                        />
                    </FormControl>
                    <BuySellDetailsMRT
                        price={selectedContract?.bid}
                        margin={margin || 0}
                        totalPrice={(
                            parseFloat(selectedContract?.bid) - margin
                        ).toFixed(3)}
                        currency="EUR"
                        deliveryPoint={state.deliverypoint}
                        contractHours={selectedContract?.contract_hours}
                        notionalValue={
                            state?.volume
                                ? (
                                      selectedContract?.contract_hours *
                                      (parseFloat(selectedContract?.bid) -
                                          margin) *
                                      parseFloat(state.volume)
                                  ).toFixed(3)
                                : 0
                        }
                        lastUpdated={selectedContract?.updated_at_cet_str}
                    />
                    {showMinMaxInput && (
                        <Box className="flex gap-3">
                            <FormControl fullWidth variant="outlined">
                                <InputLabel
                                    id="priceLabel"
                                    sx={{ bgcolor: '#F5F5F5', p: '0 3px' }}
                                >
                                    Max Price
                                </InputLabel>
                                <OutlinedInput
                                    id="price"
                                    label="Price"
                                    name="maxprice"
                                    value={state.maxprice}
                                    onChange={handleMaxChange}
                                    disabled={state.on_min}
                                    required
                                />
                            </FormControl>
                            <FormControl fullWidth variant="outlined">
                                <InputLabel
                                    id="priceLabel"
                                    sx={{ bgcolor: '#F5F5F5', p: '0 3px' }}
                                >
                                    Min Price
                                </InputLabel>
                                <OutlinedInput
                                    id="price"
                                    label="Price"
                                    name="minprice"
                                    value={state.minprice}
                                    onChange={handleMinChange}
                                    disabled={state.on_max}
                                    required
                                />
                            </FormControl>
                        </Box>
                    )}
                </Box>
            </CustomModal>
        );
    }
);
